'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import { t } from '../../../../i18n';
import Button from '../../../../components/Button/Button';
import { httpDeleteWithCsrfToken } from '../../../../utils/Http';
import { COMMENTS_URL, useReportComments } from '../ReportCommentsProvider';

import s from './CommentPanel.module.scss';

// Deletes a comment on a second, confirming click, so one click can't delete
// it by accident. `onDeleted` runs before the comment leaves the list.
const DeleteComment = ({ id, onDeleted }) => {
    const deleteRef = useRef(null);
    const confirmRef = useRef(null);
    const cancelRef = useRef(null);
    const hasAsked = useRef(false);
    const { commentDeleted } = useReportComments();
    const [isConfirming, setIsConfirming] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [failed, setFailed] = useState(false);

    // Keeps keyboard focus on the controls as they swap. Asking lands on
    // "Cancel", so pressing Enter twice doesn't delete.
    useEffect(() => {
        if (isConfirming) {
            hasAsked.current = true;
            cancelRef.current?.focus();
        } else if (hasAsked.current) {
            deleteRef.current?.focus();
        }
    }, [isConfirming]);

    // The button took its focus away while the request ran. Trying again
    // starts from it.
    useEffect(() => {
        if (failed) confirmRef.current?.focus();
    }, [failed]);

    // Whether the API deleted it.
    const send = async () => {
        try {
            const response = await httpDeleteWithCsrfToken(
                `${COMMENTS_URL}${id}/`
            );
            // A 404 says someone else deleted it first, which is just as
            // good.
            if (response.status !== 204 && response.status !== 404) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return true;
        } catch (error) {
            console.error('Failed to delete report comment:', error);
            Sentry.captureException(error);
            return false;
        }
    };

    // Once deleted, the comment leaves the list, and this with it.
    const deleteForGood = async () => {
        setFailed(false);
        setIsDeleting(true);
        if (await send()) {
            onDeleted();
            commentDeleted(id);
            return;
        }
        setIsDeleting(false);
        setFailed(true);
    };

    const cancel = () => {
        setIsConfirming(false);
        setFailed(false);
    };

    if (!isConfirming) {
        return (
            <div className={s.Delete}>
                <Button
                    ref={deleteRef}
                    modifier="Secondary"
                    size="Small"
                    isFullWidth
                    title={t('reportComments.deleteButton')}
                    aria-label={t('reportComments.delete')}
                    onClick={() => setIsConfirming(true)}
                />
            </div>
        );
    }

    return (
        <div className={s.Delete}>
            <div className={s.Actions}>
                <button
                    ref={confirmRef}
                    type="button"
                    className={s.DangerButton}
                    disabled={isDeleting}
                    onClick={deleteForGood}>
                    {t('reportComments.confirmDelete')}
                </button>
                <button
                    ref={cancelRef}
                    type="button"
                    className={s.TextButton}
                    disabled={isDeleting}
                    onClick={cancel}>
                    {t('reportComments.cancel')}
                </button>
            </div>
            {failed && (
                <p className={s.Errors} role="alert">
                    {t('reportComments.deleteFailed')}
                </p>
            )}
        </div>
    );
};

export default DeleteComment;
